import { compose } from 'redux';
import { fromJS } from 'immutable';
import { arrayMoveImmutable } from 'array-move';
import { connect, useDispatch } from 'react-redux';
import { useState, useCallback, useEffect } from 'react';

import {
  addNotification,
  NOTIFICATION_VARIANTS,
} from 'altus-redux-middlewares';

import { useModal } from 'altus-modal';

import {
  MODAL,
  EQUIPMENT_ACTIONS,
} from 'features/equipment/equipment.constants';

import {
  getToolAssembly,
  updateToolAssembly,
} from 'features/equipment/equipment.actions';

import BasePage from 'app/components/BasePageDense';
import { getActionDataStateFromState } from 'app/app.selectors';
import { ToolCategoryType } from 'features/projects/tool/tool.constants';
import ToolPickerModalContainer from 'features/projects/tool/components/ToolPickerModalContainer';
import ToolAssembliesModal from 'features/equipment/toolAssemblies/components/ToolAssembliesModal';

const toolstringItemCategories = [
  ToolCategoryType.MWL,
  ToolCategoryType.EWL,
  ToolCategoryType.ThirdParty,
];

const EditToolAssembliesModalContainer = ({
  toolId,
  openModal,
  dataState,
  setTooldId,
  toggleModal,
}) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedTools, setSelectedTools] = useState([]);
  const [toolAssemblyName, setToolAssemblyName] = useState('');
  const [toolAssemblyInfo, setToolAssemblyInfo] = useState(null);

  const [toolAssemblyItemModalOpen, toggleToolAssemblyItemModal] = useModal(
    MODAL.ADD_TOOL_TO_TOOL_ASSEMBLY,
  );

  const handleToggleModal = () => {
    setSelectedTools([]);
    setToolAssemblyName('');
    setToolAssemblyInfo(null);
    setTooldId(null);
    toggleModal();
  };

  useEffect(() => {
    if (toolId) {
      dispatch(getToolAssembly(toolId)).then((payload) => {
        setSelectedTools(
          fromJS(payload.toolAssemblyTools.map((tool) => tool.externalTool)),
        );
        setToolAssemblyInfo(payload.description);
        setToolAssemblyName(payload.name);
        setIsLoading(false);
      });
    }
  }, [toolId, dispatch]);

  const onUpdateAssemblyName = useCallback((formValue) => {
    setToolAssemblyName(formValue.name);
  }, []);

  const onAddToolEditToolAssemblies = useCallback(
    (tool) => {
      setSelectedTools((prevArray) => [...prevArray, tool]);
      dispatch(
        addNotification({
          message: 'Tool added to assembly.',
          variant: NOTIFICATION_VARIANTS.SUCCESS,
        }),
      );
    },
    [dispatch],
  );

  const onSortToolstringItem = useCallback(
    ({ oldIndex, newIndex }) => {
      if (oldIndex === newIndex) return;

      const tools = [...selectedTools];
      const newTools = arrayMoveImmutable(tools, oldIndex, newIndex);
      setSelectedTools(newTools);
    },
    [selectedTools],
  );

  const onDeleteToolstringItem = useCallback(
    (toolAssemblyTool) => {
      const tools = [...selectedTools];
      const toolIndex = tools.indexOf(toolAssemblyTool);
      tools.splice(toolIndex, 1);
      setSelectedTools(tools);
    },
    [selectedTools],
  );

  const onDuplicateItem = useCallback(
    (tool) => {
      const tools = [...selectedTools];
      const toolIndex = tools.indexOf(tool);
      tools.push(tool);
      const lastToolIndex = tools.lastIndexOf(tool);
      const newTools = arrayMoveImmutable(tools, lastToolIndex, toolIndex + 1);
      setSelectedTools(newTools);
    },
    [selectedTools],
  );

  const onSaveToolAssembly = useCallback(() => {
    const tools = selectedTools.map((tool) =>
      tool.get('id') ? tool.get('id') : tool.get('externalToolId'),
    );
    const toolAssemblyToSave = {
      toolAssemblyId: toolId,
      name: toolAssemblyName,
      description: toolAssemblyInfo,
      toolAssemblyToolsIds: tools,
    };
    dispatch(updateToolAssembly(toolAssemblyToSave));
    toggleModal();
  }, [
    selectedTools,
    dispatch,
    toolAssemblyInfo,
    toolAssemblyName,
    toolId,
    toggleModal,
  ]);

  return (
    <BasePage dataState={dataState}>
      <ToolAssembliesModal
        edit={true}
        open={openModal}
        isLoading={isLoading}
        dataState={dataState}
        toggleModal={handleToggleModal}
        title="Edit tool assembly"
        selectedTools={selectedTools}
        onDuplicateItem={onDuplicateItem}
        toolAssemblyInfo={toolAssemblyInfo}
        toolAssemblyName={toolAssemblyName}
        onSaveToolAssembly={onSaveToolAssembly}
        setToolAssemblyInfo={setToolAssemblyInfo}
        setToolAssemblyName={setToolAssemblyName}
        onUpdateAssemblyName={onUpdateAssemblyName}
        onSortToolstringItem={onSortToolstringItem}
        onDeleteToolstringItem={onDeleteToolstringItem}
        toggleToolAssemblyItemModal={toggleToolAssemblyItemModal}
      />
      <ToolPickerModalContainer
        onAddTool={onAddToolEditToolAssemblies}
        open={toolAssemblyItemModalOpen}
        onToggleModal={toggleToolAssemblyItemModal}
        enabledToolCategoryTypes={toolstringItemCategories}
      />
    </BasePage>
  );
};

const mapStateToProps = (state) => ({
  dataState: getActionDataStateFromState(
    state,
    EQUIPMENT_ACTIONS.SAVE_TOOL_ASSEMBLY,
    EQUIPMENT_ACTIONS.GET_TOOL_ASSEMBLY,
  ),
});

const mapDispatchToProps = {};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  EditToolAssembliesModalContainer,
);
